/** Safe internal path for post-login / post-signup redirect (`?next=` or `location.state.next`). */
export function sanitizeLoginNextPath(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  if (trimmed.includes('\\') || trimmed.includes('://') || trimmed.includes('@')) return null;
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return null;

  try {
    const url = new URL(trimmed, 'https://shopgnd.local');
    if (url.hostname !== 'shopgnd.local') return null;

    const pathname = url.pathname;
    if (!pathname.startsWith('/') || pathname.startsWith('//')) return null;
    if (pathname.includes('://') || pathname.includes('@')) return null;

    const params = new URLSearchParams(url.search);
    for (const key of [...params.keys()]) {
      if (key.toLowerCase() !== 'next') continue;
      const safeNested = sanitizeLoginNextPath(params.get(key));
      if (safeNested) params.set(key, safeNested);
      else params.delete(key);
    }

    const search = params.toString();
    return search ? `${pathname}?${search}` : pathname;
  } catch {
    return null;
  }
}

export function resolveLoginNextFromLocation(location: {
  search: string;
  state: unknown;
}): string | null {
  const stateNext = (location.state as { next?: string } | null)?.next;
  const queryNext = new URLSearchParams(location.search).get('next');
  return sanitizeLoginNextPath(stateNext) ?? sanitizeLoginNextPath(queryNext);
}

/** Current route (path + query) to use as the post-login return target. */
export function currentPathForLoginReturn(location: { pathname: string; search: string }): string {
  const path = `${location.pathname}${location.search}`;
  return sanitizeLoginNextPath(path) ?? location.pathname;
}

export function loginPathWithNext(next: string): string {
  const safe = sanitizeLoginNextPath(next);
  if (!safe) return '/login';
  return `/login?next=${encodeURIComponent(safe)}`;
}
